import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { format } from 'date-fns'
import { ReactComponent as CopySvg } from 'images/icons/copy.svg'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import getTestId from 'utils/getTestId'
import { IRecordedRoute } from 'utils/interfaces'

import RenameRecordedRouteModal from './RenameRecordedRouteModal/RenameRecordedRouteModal'

import style from './RecordedRouteItem.module.css'

const RecordedRouteItem = ({
  route,
  index,
  highlighted,
  onRoutesChanged,
  onClicked,
  onEditClicked,
}: {
  route: IRecordedRoute
  index: number
  highlighted: boolean
  onRoutesChanged: () => void
  onClicked: () => void
  onEditClicked: () => void
}) => {
  const { t } = useTranslation()
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [highlighted])

  const zoomZone = (zone_name: string) => {
    onClicked()
  }
  const [deleteZoneDialog, setDeleteZoneDialog] = useState(false)
  const [renameZoneDialog, setRenameZoneDialog] = useState(false)

  const deleteRecordedRoute = async () => {
    try {
      await MainApi.deleteRecordedRouteItem(route.id as number)
      setDeleteZoneDialog(false)
      onRoutesChanged()
    } catch (error) {
      console.log('Error deleting the recorded route and zone:')
    }
  }

  const copyRecordedRoute = async () => {
    try {
      if (route.id) {
        await MainApi.copyRecordedRoute(route.id as number)
        onRoutesChanged()
      }
    } catch (error) {
      console.error('Error copying recorded route:', error)
    }
  }

  return (
    <>
      <div
        ref={itemRef}
        className={highlighted ? clsx(style.tableRow, style.highlighted) : style.tableRow}
        onClick={() => zoomZone(route.properties.name)}
        key={route.id}
      >
        <span></span>
        <span className={style.tableColLeft}>{index}</span>
        <span className={style.tableColLeft}>{route.properties.name}</span>
        <span className={style.tableCol}>
          {route.properties.length ? `${route.properties.length.toFixed(2)}m` : ''}
        </span>
        <span className={style.tableCol}>
          {format(
            new Date(route.properties.recordedAt ? route.properties.recordedAt : route.properties.createdAt),
            'dd.MM.yyyy HH:mm'
          )}
        </span>
        <div className={style.zoneItemColSmall}>
          <ActionsMenu>
            <ActionsMenuItem onClick={() => onEditClicked()} data-test-id={getTestId('edit-route-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-route-title')}>
                {'Edit'}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => setRenameZoneDialog(true)} data-test-id={getTestId('edit-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                {t('common.rename')}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={copyRecordedRoute} data-test-id={getTestId('copy-zone-list')}>
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('copy-title')}>
                {t('common.copy') || 'Copy'}
              </span>
            </ActionsMenuItem>
            <ActionsMenuItem onClick={() => setDeleteZoneDialog(true)} data-test-id={getTestId('remove-zone-list')}>
              <RemoveSvg className={style.menuItemIcon} />
              <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
            </ActionsMenuItem>
          </ActionsMenu>
        </div>
      </div>
      <ConfirmDialog
        title={t('view_zones.delete_question', { filename: route.properties.name })}
        isOpen={deleteZoneDialog}
        onClose={() => setDeleteZoneDialog(false)}
        onConfirmClick={deleteRecordedRoute}
        confirmText={t('common.delete')}
        danger={true}
      />
      <RenameRecordedRouteModal
        isOpen={renameZoneDialog}
        routeZoneName={route.properties.name}
        routeId={route.id as number}
        onSave={onRoutesChanged}
        onClose={() => setRenameZoneDialog(false)}
      />
    </>
  )
}

export default RecordedRouteItem
